import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface AccessibilityState {
  // Visual accessibility
  highContrast: boolean;
  reducedMotion: boolean;
  largeText: boolean;
  
  // Focus management
  showFocusRings: boolean;
  keyboardNavigation: boolean;
  
  // Screen reader
  screenReaderOptimized: boolean;
  announcements: boolean;
  
  // User preferences
  autoplay: boolean;
  soundEffects: boolean;
}

interface AccessibilityContextType {
  state: AccessibilityState;
  toggleHighContrast: () => void;
  toggleReducedMotion: () => void;
  toggleLargeText: () => void;
  toggleScreenReaderMode: () => void;
  setKeyboardNavigation: (enabled: boolean) => void;
  announceToScreenReader: (message: string, priority?: 'polite' | 'assertive') => void;
  resetToDefaults: () => void;
}

const defaultState: AccessibilityState = {
  highContrast: false,
  reducedMotion: false,
  largeText: false,
  showFocusRings: false,
  keyboardNavigation: false,
  screenReaderOptimized: false,
  announcements: true,
  autoplay: true,
  soundEffects: true,
};

const AccessibilityContext = createContext<AccessibilityContextType | null>(null);

export function useAccessibility() {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
}

interface AccessibilityProviderProps {
  children: ReactNode;
}

export function AccessibilityProvider({ children }: AccessibilityProviderProps) {
  const [state, setState] = useState<AccessibilityState>(defaultState);

  // Load preferences from localStorage on mount
  useEffect(() => {
    const savedPreferences = localStorage.getItem('accessibility-preferences');
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        setState(prevState => ({ ...prevState, ...parsed }));
      } catch (error) {
        console.warn('Failed to parse accessibility preferences:', error);
      }
    }

    // Detect system preferences
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const prefersHighContrast = window.matchMedia('(prefers-contrast: high)').matches;
    
    setState(prevState => ({
      ...prevState,
      reducedMotion: prevState.reducedMotion || prefersReducedMotion,
      highContrast: prevState.highContrast || prefersHighContrast,
    }));
  }, []);

  // Save preferences to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('accessibility-preferences', JSON.stringify(state));
    
    // Apply CSS classes to document
    const { body } = document;
    
    // High contrast mode
    if (state.highContrast) {
      body.classList.add('high-contrast');
    } else {
      body.classList.remove('high-contrast');
    }
    
    // Reduced motion
    if (state.reducedMotion) {
      body.classList.add('reduce-motion');
    } else {
      body.classList.remove('reduce-motion');
    }
    
    // Large text
    if (state.largeText) {
      body.style.fontSize = '120%';
    } else {
      body.style.fontSize = '';
    }
    
    // Focus rings
    if (state.showFocusRings) {
      body.classList.add('show-focus-rings');
    } else {
      body.classList.remove('show-focus-rings');
    }
    
    // Keyboard navigation
    if (state.keyboardNavigation) {
      body.classList.add('keyboard-navigation');
    } else {
      body.classList.remove('keyboard-navigation');
    }
    
    // Screen reader optimization
    if (state.screenReaderOptimized) {
      body.classList.add('screen-reader-optimized');
    } else {
      body.classList.remove('screen-reader-optimized');
    }
  }, [state]);

  // Listen for keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        setKeyboardNavigation(true);
      }
    };

    const handleMouseDown = () => {
      setKeyboardNavigation(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyboard = (event: KeyboardEvent) => {
      // Only handle shortcuts when not in input fields
      if (event.target instanceof HTMLInputElement || 
          event.target instanceof HTMLTextAreaElement ||
          event.target instanceof HTMLSelectElement) {
        return;
      }

      const isCmd = event.metaKey || event.ctrlKey;
      
      switch (true) {
        // Cmd/Ctrl + K for search
        case isCmd && event.key === 'k':
          event.preventDefault();
          const searchInput = document.querySelector('[data-search-input]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
            announceToScreenReader('Search focused');
          }
          break;
          
        // Space for play/pause
        case event.key === ' ' && !event.shiftKey:
          event.preventDefault();
          const playButton = document.querySelector('[data-play-button]') as HTMLButtonElement;
          if (playButton) {
            playButton.click();
            announceToScreenReader('Playback toggled');
          }
          break;
          
        // Arrow keys for track control
        case event.altKey && event.key === 'ArrowLeft':
          event.preventDefault();
          const prevButton = document.querySelector('[data-prev-button]') as HTMLButtonElement;
          if (prevButton) {
            prevButton.click();
            announceToScreenReader('Previous track');
          }
          break;
          
        case event.altKey && event.key === 'ArrowRight':
          event.preventDefault();
          const nextButton = document.querySelector('[data-next-button]') as HTMLButtonElement;
          if (nextButton) {
            nextButton.click();
            announceToScreenReader('Next track');
          }
          break;
          
        // Escape to close modals
        case event.key === 'Escape':
          const closeButton = document.querySelector('[data-modal-close]') as HTMLButtonElement;
          if (closeButton) {
            closeButton.click();
            announceToScreenReader('Modal closed');
          }
          break;
      }
    };

    document.addEventListener('keydown', handleGlobalKeyboard);
    return () => document.removeEventListener('keydown', handleGlobalKeyboard);
  }, []);

  const toggleHighContrast = useCallback(() => {
    setState(prev => ({ ...prev, highContrast: !prev.highContrast }));
  }, []);

  const toggleReducedMotion = useCallback(() => {
    setState(prev => ({ ...prev, reducedMotion: !prev.reducedMotion }));
  }, []);

  const toggleLargeText = useCallback(() => {
    setState(prev => ({ ...prev, largeText: !prev.largeText }));
  }, []);

  const toggleScreenReaderMode = useCallback(() => {
    setState(prev => ({ ...prev, screenReaderOptimized: !prev.screenReaderOptimized }));
  }, []);

  const setKeyboardNavigation = useCallback((enabled: boolean) => {
    setState(prev => ({ ...prev, keyboardNavigation: enabled, showFocusRings: enabled }));
  }, []);

  const announceToScreenReader = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (!state.announcements) return;

    // Create or update live region
    let liveRegion = document.getElementById('accessibility-announcements');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'accessibility-announcements';
      liveRegion.setAttribute('aria-live', priority);
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'sr-only';
      document.body.appendChild(liveRegion);
    }

    // Update the live region
    liveRegion.setAttribute('aria-live', priority);
    liveRegion.textContent = message;

    // Clear after announcement
    setTimeout(() => {
      if (liveRegion) {
        liveRegion.textContent = '';
      }
    }, 1000);
  }, [state.announcements]);

  const resetToDefaults = useCallback(() => {
    setState(defaultState);
    localStorage.removeItem('accessibility-preferences');
  }, []);

  const value: AccessibilityContextType = {
    state,
    toggleHighContrast,
    toggleReducedMotion,
    toggleLargeText,
    toggleScreenReaderMode,
    setKeyboardNavigation,
    announceToScreenReader,
    resetToDefaults,
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
}

// Hook for focus management
export function useFocusManagement() {
  const { state } = useAccessibility();

  const trapFocus = useCallback((container: HTMLElement) => {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);
    
    // Focus first element
    if (firstElement) {
      firstElement.focus();
    }

    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  }, []);

  const restoreFocus = useCallback((previousElement: HTMLElement | null) => {
    if (previousElement && document.contains(previousElement)) {
      previousElement.focus();
    }
  }, []);

  return {
    trapFocus,
    restoreFocus,
    keyboardNavigation: state.keyboardNavigation,
  };
}

// Hook for screen reader utilities
export function useScreenReader() {
  const { announceToScreenReader, state } = useAccessibility();

  const announcePageChange = useCallback((pageName: string) => {
    announceToScreenReader(`Navigated to ${pageName}`, 'assertive');
  }, [announceToScreenReader]);

  const announceAction = useCallback((action: string) => {
    announceToScreenReader(action, 'polite');
  }, [announceToScreenReader]);

  const announceError = useCallback((error: string) => {
    announceToScreenReader(`Error: ${error}`, 'assertive');
  }, [announceToScreenReader]);

  const announceSuccess = useCallback((message: string) => {
    announceToScreenReader(`Success: ${message}`, 'polite');
  }, [announceToScreenReader]);

  return {
    announcePageChange,
    announceAction,
    announceError,
    announceSuccess,
    isOptimized: state.screenReaderOptimized,
  };
} 